/* eslint-disable max-lines-per-function */
import './TopbarMenu.scss'

import { FlyoutMenu, SvgIcon } from '@bob/library'
import { CTA, ctaColorTheme } from '@websrc/App/Components/CTA'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { usePath } from '@websrc/App/Hooks/usePath'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { CTAType } from '@websrc/DataMappers/mapCTA'
import { mapServicebar } from '@websrc/DataMappers/mapServicebar'
import { mapTopbar } from '@websrc/DataMappers/mapTopbar'
import { CSTopbar_LeftMenuItems_Dropdown } from '@websrc/Typings/ContentStack'
import clsx from 'clsx'
import { Link } from 'gatsby'
import React, { useEffect } from 'react'

import { checkIsActive, isButton, isCTA } from '../MainTopbar.helpers'

export const TopbarMenu: React.FC<{
  items:
    | ReturnType<typeof mapTopbar>['menu_items' | 'left_menu_items']
    | ReturnType<typeof mapServicebar>['menu_items']
  showDropDownsOnRight?: boolean
  hidePrimaryCTAs?: boolean
  hideItems?: boolean
  primaryCTAsWidth?: (width: number) => void
}> = ({ items, showDropDownsOnRight, hideItems, hidePrimaryCTAs, primaryCTAsWidth }) => {
  const path = usePath()
  const locale = useLocale()

  const menuListItemRef = React.useRef<HTMLLIElement>(null)

  useEffect(() => {
    const sendWidthUpdate = () => {
      if (menuListItemRef.current && primaryCTAsWidth) {
        primaryCTAsWidth(menuListItemRef.current.offsetWidth || 0)
      }
    }

    window.addEventListener('resize', sendWidthUpdate)
    sendWidthUpdate()
    return () => window.removeEventListener('resize', sendWidthUpdate)
  }, [primaryCTAsWidth])

  return (
    <ul className={'top-bar-menu__list'}>
      {items.map((menuItem, i) => {
        const isActive = checkIsActive(path, menuItem, locale)

        if (isButton(menuItem)) {
          return (
            <li className={'top-bar-menu__list-item'} key={i}>
              <Link
                className={clsx(
                  'top-bar-menu__item-text',
                  isActive && 'isActive',
                  hideItems && 'top-bar-menu__menu-cta hidden'
                )}
                data-text={menuItem.button.link.title}
                onClick={() =>
                  matomo.trackEvent(
                    'engagement',
                    'all',
                    ['top menu'],
                    [menuItem.button.link.title],
                    'menu'
                  )
                }
                to={menuItem.button.link.href}
              >
                {menuItem.button.link.title}
              </Link>
            </li>
          )
        }

        if (isCTA(menuItem)) {
          const ctaClass = clsx(
            isActive && 'isActive',
            menuItem.type === CTAType.PRIMARY
              ? 'coral-global-button m--type-fill m--theme-white m--size-sm'
              : 'top-bar-menu__item-text',
            menuItem.type === CTAType.PRIMARY && hidePrimaryCTAs && 'top-bar-menu__menu-cta hidden',
            menuItem.type !== CTAType.PRIMARY && hideItems && 'top-bar-menu__menu-cta hidden',
            menuItem.type === CTAType.PRIMARY && 'primaryCTA',
            menuItem.type === CTAType.TERTIARY &&
              menuItem.icon &&
              'top-bar-menu__menu-cta link-with-icon'
          )

          return (
            <li
              className={'top-bar-menu__list-item'}
              key={i}
              ref={menuItem.type === CTAType.PRIMARY ? menuListItemRef : null}
            >
              <CTA
                className={ctaClass}
                colorTheme={ctaColorTheme(menuItem.type)}
                cta={menuItem}
                data-text={menuItem?.text}
                onClick={() =>
                  matomo.trackEvent('engagement', 'all', ['top menu'], [menuItem?.text], 'menu')
                }
                prefix={
                  menuItem.icon ? (
                    <SvgIcon color={isActive ? 'blue6' : 'black'} name={menuItem.icon} size={18} />
                  ) : null
                }
                type={menuItem.type}
              />
            </li>
          )
        }

        if (!('dropdown' in menuItem)) return null

        const { links, links_divided = [] } =
          menuItem.dropdown as CSTopbar_LeftMenuItems_Dropdown['dropdown']

        const CTAContent = (
          <>
            <span
              className={clsx('top-bar-menu__item-text', isActive && 'isActive')}
              data-text={menuItem.dropdown.title}
            >
              {menuItem.dropdown.title}
            </span>
            <SvgIcon name={'ARROW_DOWN'} size={14} strokeWidth={4} />
          </>
        )

        const CTAItem = menuItem.dropdown.title_link ? (
          <Link
            className={clsx('top-bar-menu__dropdown-trigger', isActive && 'isActive')}
            onClick={() =>
              matomo.trackEvent(
                'engagement',
                'all',
                ['top menu'],
                [menuItem.dropdown.title],
                'menu'
              )
            }
            to={menuItem.dropdown.title_link}
          >
            {CTAContent}
          </Link>
        ) : (
          <div className={clsx('top-bar-menu__dropdown-trigger', isActive && 'isActive')}>
            {CTAContent}
          </div>
        )
        const allLinks = [...links, ...links_divided]
        const dividedStartIndex = links.length
        return (
          <li className={'top-bar-menu__list-item'} key={i}>
            <FlyoutMenu
              buttonElement={CTAItem}
              className={clsx(hideItems && 'top-bar-menu__menu-cta hidden')}
              showOnRight={showDropDownsOnRight}
            >
              <ul className={'top-bar-menu__dropdown-wrapper'}>
                {allLinks.map(({ title, href }, linkI) => (
                  <li
                    className={clsx(
                      'top-bar-menu__dropdown-item-wrapper',
                      linkI === dividedStartIndex ? 'top-bar-menu__dropdown-item--divider' : ''
                    )}
                    key={`link-${linkI}`}
                    onClick={(e) => {
                      const anchor = e.currentTarget.querySelector('a')
                      if (anchor) {
                        // Simulate anchor click
                        anchor.click()
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const anchor = e.currentTarget.querySelector('a')
                        if (anchor) anchor.click()
                      }
                    }}
                    role={'link'}
                    tabIndex={0}
                  >
                    <a
                      className={'top-bar-menu__item-text'}
                      data-text={title}
                      href={href}
                      onClick={(e) => {
                        // Optional: prevent duplicate navigation if already triggered from <li>
                        e.stopPropagation()

                        matomo.trackEvent(
                          'engagement',
                          'all',
                          ['top menu'],
                          [menuItem.dropdown.title, title],
                          'menu'
                        )
                      }}
                    >
                      {title}
                    </a>
                  </li>
                ))}
              </ul>
            </FlyoutMenu>
          </li>
        )
      })}
    </ul>
  )
}
